import { useEffect } from 'react';
import { useLingui } from '@lingui/react/macro';

import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import { OptionalLabel } from '@/design_system/Label/OptionalLabel';
import { useUpdateArticle } from '@/models/article';
import {
  getSelectableProductAttributeValues,
  getSelectedProductAttributeValueIdsWithAutoSelectedSingleChildren,
  ProductAttribute,
} from '@/models/productAttribute';
import { useDatabaseTranslation } from '@/services/i18n';

import { useArticleContext } from '../../contexts/ArticleContext';

import { useProductAndUniqueArticleInputs } from './useProductAndUniqueArticleInputs';

const getNextProductAttributeValues = ({
  optionId,
  selectedValueIds,
  productAttribute,
  productAttributes,
}: {
  optionId: string | null;
  selectedValueIds: string[];
  productAttribute: ProductAttribute;
  productAttributes: ProductAttribute[];
}) => {
  const selectedIdsWithoutCurrentAttribute = selectedValueIds.filter(
    (valueId) => !productAttribute.productAttributeValues.some((value) => value.id === valueId)
  );
  const nextSelectedValueIds = optionId
    ? [...selectedIdsWithoutCurrentAttribute, optionId]
    : selectedIdsWithoutCurrentAttribute;
  const filteredSelectedValueIds =
    getSelectedProductAttributeValueIdsWithAutoSelectedSingleChildren({
      productAttributes,
      selectedValueIds: nextSelectedValueIds,
    });

  return productAttributes
    .flatMap((attribute) => attribute.productAttributeValues)
    .filter((value) => filteredSelectedValueIds.includes(value.id));
};

export const ProductAttributeValue = ({
  productAttribute,
}: {
  productAttribute: ProductAttribute;
}) => {
  const { t } = useLingui();
  const { _db } = useDatabaseTranslation();
  const { request, article, errors, productAttributes } = useArticleContext();
  const productAndUniqueArticleInputs = useProductAndUniqueArticleInputs();
  const { mutate: updateArticle } = useUpdateArticle({
    articleId: article.id,
    requestId: request.id,
  });
  const selectedValueIds = article.productAttributeValues.map((value) => value.id);

  const isRootProductAttribute = !productAttribute.parentProductAttributeValueId;

  const isParentSelected =
    isRootProductAttribute ||
    selectedValueIds.includes(productAttribute.parentProductAttributeValueId!);

  const options = getSelectableProductAttributeValues({
    productAttributeValues: productAttribute.productAttributeValues,
    selectedValueIds,
  });

  const hasProductAttributeValues = article.productAttributeValues.length > 0;
  const singleOptionId = hasProductAttributeValues && options.length === 1 ? options[0].id : null;
  const isSingleOptionSelected = !!singleOptionId && selectedValueIds.includes(singleOptionId);

  const selectedValue =
    article.productAttributeValues.find((value) =>
      productAttribute.productAttributeValues.some((option) => option.id === value.id)
    ) ?? (singleOptionId ? options[0] : null);

  useEffect(() => {
    if (
      !productAttribute.enabled ||
      !isParentSelected ||
      !singleOptionId ||
      isSingleOptionSelected
    ) {
      return;
    }

    updateArticle({
      data: {
        productAttributeValues: getNextProductAttributeValues({
          optionId: singleOptionId,
          selectedValueIds,
          productAttribute,
          productAttributes: productAttributes ?? [],
        }),
      },
    });
  }, [singleOptionId, isSingleOptionSelected, isParentSelected, productAttribute.enabled]);

  if (!productAttribute.enabled || !isParentSelected) {
    return null;
  }

  const isDisabled =
    !!singleOptionId ||
    (!!article.product &&
      article.product.productAttributeValues.some((value) =>
        productAttribute.productAttributeValues.some((option) => option.id === value.id)
      ));

  return (
    <InputSelect
      variant="select"
      label={
        <>
          {_db(productAttribute.name)}
          {!isRootProductAttribute && !productAttribute.required && !isDisabled && (
            <OptionalLabel />
          )}
        </>
      }
      placeholder={t({
        id: 'article.form.product-attribute-value.placeholder',
        message: 'Select a value',
      })}
      isDisabled={isDisabled}
      isClearable={!productAttribute.required && !isDisabled}
      value={selectedValue}
      options={options.map((option) => ({
        ...option,
        productAttribute: productAttribute,
      }))}
      getOptionValue={(option) => option.id}
      getOptionLabel={(option) => _db(option.name)}
      onChange={(option) =>
        updateArticle({
          data: {
            productAttributeValues: getNextProductAttributeValues({
              optionId: option?.id ?? null,
              selectedValueIds,
              productAttribute,
              productAttributes: productAttributes ?? [],
            }),
          },
        })
      }
      error={
        isRootProductAttribute && errors.details?.product
          ? productAndUniqueArticleInputs.errorMessage
          : errors.details?.productAttributes?.find(({ id }) => id === productAttribute.id)
                ?.missingValue
            ? t({
                id: 'article.form.product-attribute-value.error',
                message: 'Please select a value',
              })
            : undefined
      }
      style={{ flex: 1 }}
    />
  );
};
